// src/pages/UserAccountsPage.tsx
//
// ★★ User account administration: who may sign in, as which role, with which scope. System Administrator only.
//
// ══ WHAT THIS SCREEN IS, AND WHAT IT IS NOT ══
//
// The read-only "Accounts and groups" screen beside it is for PSD and changes nothing: the central accounts
// service makes those changes. This one is for the System Administrator, for an estate where PRS2 itself has to
// hold the roster. It adds an account, changes a role or a scope, and switches an account off or back on.
//
// ⚠ A role is only a label. What an account can SEE and APPROVE is its scope rows, so the form shows both
// together and prefills the scope from the role's standard one. An administrator edits rows; they never invent
// them from nothing.
//
// ⚠ Three rules are shown as they bite, because the server enforces them and a screen that hid them would let a
// person click and be refused: you cannot change your own role or switch yourself off, the last active
// administrator stays, and an account the central service disabled is re-enabled there, not here.
//
// ⚠ Every bound on a field is the server's own, read from the payload's `validation` block. This file holds no
// number of its own.
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { SgdsAlert, SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useAuth } from '../state/AuthContext'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult, type FormResultState } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { RoleBadge, StateBadge } from '../components/StatusBadge'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatInstant } from '../lib/format'
import { plainProse, roleWords } from '../lib/plain-words'
import type { AccountChange, PublishedBounds, ScopeBillTypeValue, UserAccountBounds, UserAccountDetail, UserRoleTemplate, UserScopeRow } from '../lib/types'

const ANY = 'ANY'
const PAGE_SIZE = 25

/** One label per bounded field: read by `boundsProblems` for its messages and by the field that shows it. */
const BOUNDED_FIELDS = {
  email: 'Email address',
  displayName: 'Name',
  note: 'Note (optional)',
} as const

const BILL_TYPE_LABELS: Readonly<Record<ScopeBillTypeValue, string>> = {
  ALL: 'All bill types',
  NEW: 'New bills',
  UPWARD_ADJUSTMENT: 'Upward adjustments',
  DOWNWARD_ADJUSTMENT: 'Downward adjustments',
}

const LEVEL_LABELS: Readonly<Record<0 | 1 | 2, string>> = {
  0: 'View only',
  1: 'Level 1 approver',
  2: 'Level 2 approver',
}

const ACTION_LABELS: Readonly<Record<AccountChange['action'], string>> = {
  CREATED: 'Account created',
  UPDATED: 'Details changed',
  DEACTIVATED: 'Switched off',
  REACTIVATED: 'Switched back on',
  PASSWORD_SET: 'Password set',
}

/** Dollars typed by a person, as the whole cents the server stores. Empty is no ceiling, which is not zero. */
function centsFromDollars(text: string): number | null | 'invalid' {
  const trimmed = text.trim()
  if (trimmed.length === 0) return null
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return 'invalid'
  return Math.round(Number(trimmed) * 100)
}

function dollarsFromCents(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2)
}

interface DraftScopeRow {
  readonly agencyCode: string
  readonly billType: ScopeBillTypeValue
  readonly approvalLevel: 0 | 1 | 2
  readonly ceiling: string
}

const toDraft = (rows: readonly UserScopeRow[]): DraftScopeRow[] =>
  rows.map((r) => ({ agencyCode: r.agencyCode, billType: r.billType, approvalLevel: r.approvalLevel, ceiling: dollarsFromCents(r.approvalLimitCents) }))

type Editing = { readonly kind: 'none' } | { readonly kind: 'new' } | { readonly kind: 'edit'; readonly id: string }

export function UserAccountsPage() {
  const { user: me } = useAuth()
  const roles = useApi(() => api.userRoles(), [])
  const [search, setSearch] = useState('')
  const [searchDraft, setSearchDraft] = useState('')
  const [roleFilter, setRoleFilter] = useState(ANY)
  const [agencyFilter, setAgencyFilter] = useState(ANY)
  const [statusFilter, setStatusFilter] = useState(ANY)
  const [offset, setOffset] = useState(0)

  const list = useApi(
    () =>
      api.userAccounts({
        ...(search.length > 0 && { search }),
        ...(roleFilter !== ANY && { role: roleFilter }),
        ...(agencyFilter !== ANY && { agency: agencyFilter }),
        ...(statusFilter !== ANY && { status: statusFilter as 'active' | 'inactive' }),
        limit: PAGE_SIZE,
        offset,
      }),
    [search, roleFilter, agencyFilter, statusFilter, offset],
  )
  const pager = useOffsetPager(PAGE_SIZE, list.data?.total)
  useEffect(() => {
    if (pager.offset !== offset) setOffset(pager.offset)
  }, [pager.offset, offset])

  const [editing, setEditing] = useState<Editing>({ kind: 'none' })
  const rows = list.data?.users ?? []
  const validation = list.data?.validation ?? roles.data?.validation

  const applyFilter = (change: () => void) => {
    change()
    pager.reset()
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">User accounts</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Add people, give them a role and a scope, and switch accounts off or back on. A person can sign in only if they
            have an account here. Every change is recorded with who made it, what it was before, and why.
          </p>
        </div>
        <SgdsButton variant="primary" onClick={() => setEditing({ kind: 'new' })} disabled={editing.kind !== 'none'}>
          Add a user
        </SgdsButton>
      </div>

      {roles.data !== undefined && (
        <SgdsAlert show variant="info" title="A role is only a label">
          {roles.data.note}
        </SgdsAlert>
      )}

      {editing.kind !== 'none' && roles.data !== undefined && (
        <AccountForm
          key={editing.kind === 'edit' ? editing.id : 'new'}
          editing={editing}
          templates={roles.data.roles}
          agencies={roles.data.agencies}
          validation={validation}
          myUserId={me?.userId}
          onClose={() => setEditing({ kind: 'none' })}
          onSaved={() => {
            setEditing({ kind: 'none' })
            list.reload()
          }}
        />
      )}

      <form
        className="pt-card pt-controlbar pt-no-print"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          applyFilter(() => setSearch(searchDraft.trim()))
        }}
      >
        <SgdsInput
          placeholder=""
          label="Search by name or email"
          value={searchDraft}
          onSgdsInput={(e) => setSearchDraft(targetValue(e))}
        />
        <SgdsSelect label="Role" value={roleFilter} onSgdsChange={(e) => applyFilter(() => setRoleFilter(targetValue(e)))}>
          <SgdsSelectOption value={ANY}>All roles</SgdsSelectOption>
          {(roles.data?.roles ?? []).map((r) => (
            <SgdsSelectOption key={r.role} value={r.role}>
              {r.label}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
        <SgdsSelect label="Agency" value={agencyFilter} onSgdsChange={(e) => applyFilter(() => setAgencyFilter(targetValue(e)))}>
          <SgdsSelectOption value={ANY}>All agencies</SgdsSelectOption>
          {(roles.data?.agencies ?? []).map((a) => (
            <SgdsSelectOption key={a} value={a}>
              {a}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
        <SgdsSelect label="Status" value={statusFilter} onSgdsChange={(e) => applyFilter(() => setStatusFilter(targetValue(e)))}>
          <SgdsSelectOption value={ANY}>Active and switched off</SgdsSelectOption>
          <SgdsSelectOption value="active">Active</SgdsSelectOption>
          <SgdsSelectOption value="inactive">Switched off</SgdsSelectOption>
        </SgdsSelect>
        <SgdsButton type="submit" variant="outline">
          Search
        </SgdsButton>
      </form>

      {list.error !== null ? (
        <ErrorCard title="Could not load user accounts" message={list.error} onRetry={list.reload} />
      ) : list.initialLoading ? (
        <LoadingCard label="Loading user accounts" rows={5} />
      ) : rows.length === 0 ? (
        <EmptyCard title="No accounts match" message="Nothing matches these filters. Clear the search or choose All to see everyone." />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={list.loading}>
            <table className="pt-table">
              <caption className="sr-only">User accounts, in email order</caption>
              <thead>
                <tr>
                  <th scope="col" data-unsortable="the list is always in email order, because the paging walks that one order">Person</th>
                  <th scope="col" data-unsortable="the list is always in email order, a fixed order the paging relies on">Role</th>
                  <th scope="col" data-unsortable="the list is always in email order, a fixed order the paging relies on">Agency</th>
                  <th scope="col" data-unsortable="the list is always in email order, a fixed order the paging relies on">Scope rows</th>
                  <th scope="col" data-unsortable="the list is always in email order, a fixed order the paging relies on">Status</th>
                  <th scope="col" data-unsortable="the list is always in email order, a fixed order the paging relies on">Last changed</th>
                  <th scope="col" data-unsortable="a column of buttons, with nothing to order by">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="font-semibold">{u.displayName}</div>
                      <div className="text-sm text-[var(--sgds-body-color-subtle)]">{u.email}</div>
                    </td>
                    <td>
                      <RoleBadge role={u.role} />
                    </td>
                    <td>{u.agencyCode}</td>
                    <td>{u.scopeCount}</td>
                    <td>
                      <StateBadge value={u.isActive ? 'ACTIVE' : 'INACTIVE'} />
                      {!u.isActive && (
                        <div className="text-xs text-[var(--sgds-body-color-subtle)]">
                          {u.disabledBy === 'CAM' ? 'Switched off by the central accounts service' : 'Switched off here'}
                        </div>
                      )}
                    </td>
                    <td>{formatInstant(u.updatedAt)}</td>
                    <td>
                      <SgdsButton
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ kind: 'edit', id: u.id })}
                        disabled={editing.kind !== 'none'}
                      >
                        Open
                      </SgdsButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pager.pageCount !== undefined && pager.pageCount > 1 && (
            <Pagination pager={pager} label="user accounts" total={list.data?.total} shown={rows.length} />
          )}
        </div>
      )}
    </div>
  )
}

interface AccountFormProps {
  readonly editing: { readonly kind: 'new' } | { readonly kind: 'edit'; readonly id: string }
  readonly templates: readonly UserRoleTemplate[]
  readonly agencies: readonly string[]
  readonly validation: UserAccountBounds | undefined
  readonly myUserId: string | undefined
  readonly onClose: () => void
  readonly onSaved: () => void
}

function AccountForm({ editing, templates, agencies, validation, myUserId, onClose, onSaved }: AccountFormProps) {
  const isNew = editing.kind === 'new'
  const detail = useApi(
    () => (editing.kind === 'edit' ? api.userAccount(editing.id) : Promise.resolve(null)),
    [editing.kind === 'edit' ? editing.id : 'new'],
  )
  const existing: UserAccountDetail | null = detail.data?.user ?? null
  const changes: readonly AccountChange[] = detail.data?.changes ?? []
  const isSelf = existing !== null && myUserId !== undefined && existing.id === myUserId

  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState('')
  const [agencyCode, setAgencyCode] = useState('')
  const [scopes, setScopes] = useState<DraftScopeRow[]>([])
  const [scopesEdited, setScopesEdited] = useState(false)
  const [note, setNote] = useState('')
  const [loadedId, setLoadedId] = useState<string | null>(null)

  // Fill the form from the account once, when it arrives. Never again: a reload after Save must not overwrite typing.
  useEffect(() => {
    if (existing !== null && loadedId !== existing.id) {
      setLoadedId(existing.id)
      setEmail(existing.email)
      setDisplayName(existing.displayName)
      setRole(existing.role)
      setAgencyCode(existing.agencyCode)
      setScopes(toDraft(existing.scopes))
      setScopesEdited(true)
    }
  }, [existing, loadedId])

  const template = useMemo(() => templates.find((t) => t.role === role), [templates, role])

  const chooseRole = (next: string) => {
    setRole(next)
    const t = templates.find((x) => x.role === next)
    if (t === undefined) return
    // The standard scope is a starting point. Replace the rows only if the person has not edited them by hand.
    if (isNew && !scopesEdited) {
      setAgencyCode(t.defaultAgency)
      setScopes(toDraft(t.defaultScope))
    }
  }

  // Narrowed views of the one published block, so each `boundsProblems` call names only the fields it checks.
  const fieldBounds: PublishedBounds<'email' | 'displayName' | 'note'> | undefined = validation
  const reasonBounds: PublishedBounds<'reason'> | undefined = validation
  const problems = boundsProblems(
    fieldBounds,
    { email: email.trim(), displayName: displayName.trim(), note: note.trim() },
    BOUNDED_FIELDS,
    {
      notSubmitted: isNew ? [] : ['email'],
    },
  )

  const parsedScopes = scopes.map((s) => ({ ...s, cents: centsFromDollars(s.ceiling) }))
  const ceilingProblem = parsedScopes.some((s) => s.cents === 'invalid')
    ? 'A ceiling is a dollar amount such as 3000 or 3000.50, or empty for no ceiling.'
    : null
  const scopeProblem =
    role === 'SYSTEM_ADMIN'
      ? scopes.length > 0
        ? 'A System Administrator holds no bill grants. Remove the rows, or choose another role.'
        : null
      : scopes.length === 0
        ? 'Add at least one scope row, or this account can see nothing.'
        : null

  const canSave =
    role.length > 0 &&
    agencyCode.length > 0 &&
    displayName.trim().length > 0 &&
    (!isNew || email.trim().length > 0) &&
    !problems.any &&
    ceilingProblem === null &&
    scopeProblem === null

  const [confirming, setConfirming] = useState<string | null>(null)
  const [state, setState] = useState<FormResultState>({ status: 'idle' })

  const body = () => ({
    ...(isNew && { email: email.trim() }),
    displayName: displayName.trim(),
    role,
    agencyCode,
    scopes: parsedScopes.map((s) => ({
      agencyCode: s.agencyCode,
      billType: s.billType,
      approvalLevel: s.approvalLevel,
      approvalLimitCents: s.cents === 'invalid' ? null : s.cents,
    })),
    ...(note.trim().length > 0 && { note: note.trim() }),
  })

  const handleSave = (event: FormEvent) => {
    event.preventDefault()
    if (!canSave) return
    const roleName = roleWords(role)
    setConfirming(
      isNew
        ? `Create an account for ${email.trim().toLowerCase()} as ${roleName} at ${agencyCode}, with ${scopes.length} scope ${scopes.length === 1 ? 'row' : 'rows'}. They can sign in as soon as they have an identity with this email address.`
        : existing !== null && existing.role !== role
          ? `Change ${existing.displayName} from ${roleWords(existing.role)} to ${roleName}. Their scope is replaced with the rows shown, and they are signed out everywhere so the change applies at once.`
          : `Save the changes to ${existing?.displayName ?? 'this account'}. If their scope changed they are signed out everywhere so it applies at once.`,
    )
  }

  const performSave = async () => {
    setConfirming(null)
    setState({ status: 'submitting' })
    try {
      if (isNew) await api.createUserAccount(body())
      else if (existing !== null) await api.updateUserAccount(existing.id, body())
      onSaved()
    } catch (error) {
      setState({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this account.' })
    }
  }

  const [toggling, setToggling] = useState<'off' | 'on' | null>(null)
  const [reason, setReason] = useState('')
  const reasonProblem = boundsProblems(reasonBounds, { reason: reason.trim() }, { reason: 'Reason' }, {}).of.reason
  const [toggleState, setToggleState] = useState<FormResultState>({ status: 'idle' })

  const performToggle = async () => {
    if (existing === null || toggling === null) return
    setToggleState({ status: 'submitting' })
    try {
      await api.setUserAccountActive(existing.id, toggling === 'on', reason.trim())
      onSaved()
    } catch (error) {
      setToggleState({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not change this account.' })
      setToggling(null)
    }
  }

  const camHold = existing !== null && !existing.isActive && existing.disabledBy === 'CAM'

  return (
    <div className="pt-card flex flex-col gap-5 pt-no-print">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h2 className="text-xl">{isNew ? 'Add a user' : (existing?.displayName ?? 'Loading account')}</h2>
        <SgdsButton variant="outline" size="sm" onClick={onClose}>
          Close
        </SgdsButton>
      </div>

      {!isNew && detail.error !== null && <ErrorCard title="Could not load this account" message={detail.error} onRetry={detail.reload} />}
      {!isNew && detail.initialLoading && <LoadingCard label="Loading account" rows={3} />}

      {(isNew || existing !== null) && (
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSave}>
          {isNew ? (
            <BoundedField
              label={BOUNDED_FIELDS.email}
              example="e.g. jane.tan@agency.gov.sg"
              value={email}
              onValueChange={setEmail}
              bounds={validation?.email}
              problem={problems.of.email}
              required
            />
          ) : (
            <div className="flex flex-col gap-1 text-sm">
              <span className="font-semibold">Email address</span>
              <span>{existing?.email}</span>
              <span className="text-[var(--sgds-body-color-subtle)]">
                An email cannot be changed, because the person signs in with it. To move someone, add a new account and switch this one off.
              </span>
            </div>
          )}
          <BoundedField
            label={BOUNDED_FIELDS.displayName}
            value={displayName}
            onValueChange={setDisplayName}
            bounds={validation?.displayName}
            problem={problems.of.displayName}
            required
          />
          <SgdsSelect
            label="Role"
            value={role}
            disabled={isSelf}
            onSgdsChange={(e) => chooseRole(targetValue(e))}
            {...(isSelf ? { hintText: 'You cannot change your own role. Ask another System Administrator.' } : {})}
          >
            {isNew && <SgdsSelectOption value="">Choose a role</SgdsSelectOption>}
            {templates.map((t) => (
              <SgdsSelectOption key={t.role} value={t.role}>
                {t.label}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>
          <SgdsSelect label="Home agency" value={agencyCode} disabled={isSelf} onSgdsChange={(e) => setAgencyCode(targetValue(e))}>
            {isNew && <SgdsSelectOption value="">Choose an agency</SgdsSelectOption>}
            {agencies.map((a) => (
              <SgdsSelectOption key={a} value={a}>
                {a}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>

          <fieldset className="sm:col-span-2 flex flex-col gap-3" disabled={isSelf}>
            <legend className="font-semibold">What this account can see and approve</legend>
            <p className="text-sm text-[var(--sgds-body-color-subtle)]">
              One row per agency and bill type. View only can read bills; level 1 and level 2 can approve them, up to the ceiling. Leave a
              ceiling empty for no ceiling.
            </p>
            {scopes.map((row, index) => (
              <div key={index} className="grid gap-3 sm:grid-cols-5 items-end">
                <SgdsSelect
                  label="Agency"
                  value={row.agencyCode}
                  onSgdsChange={(e) => {
                    setScopesEdited(true)
                    setScopes(scopes.map((r, i) => (i === index ? { ...r, agencyCode: targetValue(e) } : r)))
                  }}
                >
                  {agencies.map((a) => (
                    <SgdsSelectOption key={a} value={a}>
                      {a}
                    </SgdsSelectOption>
                  ))}
                </SgdsSelect>
                <SgdsSelect
                  label="Bill type"
                  value={row.billType}
                  onSgdsChange={(e) => {
                    setScopesEdited(true)
                    setScopes(scopes.map((r, i) => (i === index ? { ...r, billType: targetValue(e) as ScopeBillTypeValue } : r)))
                  }}
                >
                  {(Object.keys(BILL_TYPE_LABELS) as ScopeBillTypeValue[]).map((b) => (
                    <SgdsSelectOption key={b} value={b}>
                      {BILL_TYPE_LABELS[b]}
                    </SgdsSelectOption>
                  ))}
                </SgdsSelect>
                <SgdsSelect
                  label="Level"
                  value={String(row.approvalLevel)}
                  onSgdsChange={(e) => {
                    setScopesEdited(true)
                    setScopes(scopes.map((r, i) => (i === index ? { ...r, approvalLevel: Number(targetValue(e)) as 0 | 1 | 2 } : r)))
                  }}
                >
                  {([0, 1, 2] as const).map((l) => (
                    <SgdsSelectOption key={l} value={String(l)}>
                      {LEVEL_LABELS[l]}
                    </SgdsSelectOption>
                  ))}
                </SgdsSelect>
                <SgdsInput
                  placeholder=""
                  label="Ceiling ($, optional)"
                  value={row.ceiling}
                  onSgdsInput={(e) => {
                    setScopesEdited(true)
                    setScopes(scopes.map((r, i) => (i === index ? { ...r, ceiling: targetValue(e) } : r)))
                  }}
                />
                <SgdsButton
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setScopesEdited(true)
                    setScopes(scopes.filter((_, i) => i !== index))
                  }}
                >
                  Remove row
                </SgdsButton>
              </div>
            ))}
            {ceilingProblem !== null && <p role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)]">{ceilingProblem}</p>}
            {scopeProblem !== null && <p role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)]">{scopeProblem}</p>}
            <div className="flex gap-2 flex-wrap">
              <SgdsButton
                variant="outline"
                size="sm"
                disabled={role === 'SYSTEM_ADMIN' || agencies.length === 0}
                onClick={() => {
                  setScopesEdited(true)
                  setScopes([...scopes, { agencyCode: agencyCode || agencies[0] || '', billType: 'ALL', approvalLevel: 0, ceiling: '' }])
                }}
              >
                Add a row
              </SgdsButton>
              {template !== undefined && role !== 'SYSTEM_ADMIN' && (
                <SgdsButton
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setScopes(toDraft(template.defaultScope))
                    setScopesEdited(false)
                  }}
                >
                  Use the standard rows for {template.label}
                </SgdsButton>
              )}
            </div>
          </fieldset>

          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />

          {confirming !== null && (
            <ConfirmWrite summary={confirming} confirmLabel="Yes, save it" onConfirm={() => void performSave()} onCancel={() => setConfirming(null)} />
          )}
          <FormResult state={state} className="sm:col-span-2" />
          {!isSelf && (
            <div className="sm:col-span-2">
              <SgdsButton type="submit" variant="primary" disabled={!canSave || confirming !== null} loading={state.status === 'submitting'}>
                {isNew ? 'Create account' : 'Save changes'}
              </SgdsButton>
            </div>
          )}
          {isSelf && (
            <p className="sm:col-span-2 text-sm text-[var(--sgds-body-color-subtle)]">
              This is your own account. You can read it, but your role, agency and scope are changed by another System Administrator.
            </p>
          )}
        </form>
      )}

      {existing !== null && !isSelf && (
        <div className="flex flex-col gap-3 border-t border-[var(--sgds-border-color-default)] pt-4">
          <h3 className="text-lg">{existing.isActive ? 'Switch this account off' : 'Switch this account back on'}</h3>
          {camHold ? (
            <SgdsAlert show variant="warning" title="Switched off by the central accounts service">
              The central accounts service switched this account off, and it decides account status. Ask it to switch the account back on;
              doing it here would be undone the next time it reconciles.
            </SgdsAlert>
          ) : (
            <>
              <BoundedField
                label="Reason"
                value={reason}
                onValueChange={setReason}
                bounds={validation?.reason}
                problem={reasonProblem}
                required
              />
              {toggling !== null && (
                <ConfirmWrite
                  summary={
                    toggling === 'off'
                      ? `Switch off ${existing.displayName}. They are signed out everywhere and cannot sign in until an administrator switches them back on.`
                      : `Switch ${existing.displayName} back on. They can sign in again with the role and scope shown here.`
                  }
                  confirmLabel={toggling === 'off' ? 'Yes, switch it off' : 'Yes, switch it on'}
                  variant={toggling === 'off' ? 'danger' : 'primary'}
                  busy={toggleState.status === 'submitting'}
                  onConfirm={() => void performToggle()}
                  onCancel={() => setToggling(null)}
                />
              )}
              <FormResult state={toggleState} />
              <div>
                <SgdsButton
                  variant={existing.isActive ? 'danger' : 'primary'}
                  disabled={reason.trim().length === 0 || reasonProblem !== null || toggling !== null}
                  onClick={() => setToggling(existing.isActive ? 'off' : 'on')}
                >
                  {existing.isActive ? 'Switch off' : 'Switch back on'}
                </SgdsButton>
              </div>
            </>
          )}
        </div>
      )}

      {existing !== null && (
        <div className="flex flex-col gap-2 border-t border-[var(--sgds-border-color-default)] pt-4">
          <h3 className="text-lg">Change history</h3>
          {changes.length === 0 ? (
            <p className="text-sm text-[var(--sgds-body-color-subtle)]">No changes have been made here yet.</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {changes.map((c) => (
                <li key={c.id} className="text-sm">
                  <span className="font-semibold">{ACTION_LABELS[c.action]}</span> by {c.actorEmail} on {formatInstant(c.occurredAt)}
                  {c.note !== null && <span>: {plainProse(c.note)}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  )
}

